import { Block, BlockConfig } from "@/types/block";
import { AnyObject, OptionalKeys } from "@/types";
import { init } from "@paralleldrive/cuid2";
import { create } from "free-style";
import { startCase } from "lodash";
import clsx, { ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

// Export all utility functions and types
export * from "./block";
export * from "./guard";
export * from "./popover";
export * from "./style";
export * from "./theme";

/**
 * Creates a unique ID generator with a specified length
 * @returns A function that generates unique IDs
 */
export const createId = init({
  length: 8,
});

/**
 * Creates a root block for the page builder
 * @returns A root block object with default properties
 */
export const createRootBlock = () => {
  return {
    id: "root",
    type: "root",
    parentId: "",
    children: [],
    settings: {},
  };
};

/**
 * Creates a content object from an array of blocks
 * @param blocks - Array of blocks to include in the content
 * @returns A content object with blocks organized by ID and parent-child relationships
 */
export const createContent = (blocks: Block[]) => {
  const content: Record<string, Block> = {
    ["root"]: createRootBlock(),
  };

  // Add block data to content
  blocks.forEach((block) => {
    content[block.id] = block;
  });

  // Add the block to the parent's children
  blocks.forEach((block) => {
    content[block.parentId].children.push(block.id);
  });

  return content;
};

/**
 * Checks if the content has no blocks (empty)
 * @param content - The content object to check
 * @returns True if the content has no blocks, false otherwise
 */
export const isEmptyContent = (content: Record<string, Block>) => {
  return !content.root.children.length;
};

/**
 * Creates a block with default values for optional properties
 * @param config - The block configuration
 * @returns A block object with all required properties
 */
export const createBlock = <T extends object = AnyObject>(
  config: OptionalKeys<Block<T>, "id" | "children">
) => {
  return {
    id: config.id ?? createId(),
    children: config.children ?? [],
    ...config,
  };
};

/**
 * Creates a block configuration with default values for optional properties
 * @param config - The block configuration options
 * @returns A complete block configuration with all required properties
 */
export const createBlockConfig = <T extends object>(
  config: OptionalKeys<BlockConfig<T>, "label" | "settings" | "controls">
) => {
  return {
    label: config.label ?? startCase(config.type),
    settings: config.settings ?? {},
    controls: config.controls ?? [],
    ...config,
  };
};

/**
 * Creates a style utility for registering and retrieving styles
 * @returns An object with register and get methods for managing styles
 */
export const createStyle = () => {
  const style = create();
  const register = style.registerStyle.bind(style);
  const get = style.getStyles.bind(style);

  return {
    register,
    get,
  };
};

/**
 * Combines multiple class values and merges Tailwind classes
 * @param inputs - Class values to combine
 * @returns A string of combined and merged class names
 */
export function classNames(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Sorts an array of items based on a provided order of IDs
 * @param ids - Array of IDs defining the desired order
 * @param data - Array of items to be sorted
 * @returns A new sorted array of items
 */
export function sortRepeatableItems(ids: string[], data: any[]) {
  if (!ids.length || !data.length) {
    return data;
  }

  const orderMap = new Map(ids.map((id, index) => [id, index]));
  return data.sort((a, b) => {
    const indexA = orderMap.get(a.id);
    const indexB = orderMap.get(b.id);

    // If both items are in the map, sort by their position
    if (indexA !== undefined && indexB !== undefined) {
      return indexA - indexB;
    }

    // If only one item is in the map, prioritize it
    if (indexA !== undefined) return -1;
    if (indexB !== undefined) return 1;

    // If neither item is in the map, maintain their relative order
    return 0;
  });
}

/**
 * Type-safe version of Object.keys that preserves the key types
 * @param obj - The object to get keys from
 * @returns An array of the object's keys with proper typing
 */
export function objectKeys<T extends object>(obj: T) {
  return Object.keys(obj) as Array<keyof T>;
}

/**
 * Converts bytes to a human-readable file size string
 * @param bytes - The size in bytes
 * @returns A formatted string representing the size (e.g., "1.5 MB")
 */
export function bytesToSize(bytes: number): string {
  const sizes: string[] = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes === 0) return "n/a";
  const i: number = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString());
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
}

/**
 * Converts a file to a base64 string
 * @param file The file to convert
 * @returns A promise that resolves to the base64 string
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Converts an image URL to a base64 string
 * @param url The URL of the image
 * @returns A promise that resolves to the base64 string
 */
export const urlToBase64 = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await fileToBase64(blob as File);
  } catch (error) {
    console.error("Error converting URL to base64:", error);
    throw error;
  }
};

/**
 * Generates a unique ID for media items
 * @returns A unique ID string
 */
export const generateUniqueId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};
